import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Source, SourceService, TemplateType, TemplateTypeService } from "../../../../swaggerServices";
import { ListOfSource } from "../../../../swaggerServices/model/listOfSource";
import { UserInfoService } from "../../user-info.service";
import { cloneDeep } from "lodash-es";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { ClientNotificationService } from '../../client-notification.service';
import { environment } from '../../../environments/environment';
import { ConfigStateService } from '../../config/config-state.service';
import { TenantConfigService } from '../../../shared/services/tenant-config.service';
import { Subscription } from 'rxjs';
import { TaxReturnTypeSubTypeService } from '../../../shared/util/tax-return-type-subtype.service';
import { TaxReturnBuilderService } from '../tax-return-builder/tax-return-builder.service';
import { ICategory } from '../tax-return-builder/tax-return-builder';
import { TaxYearsService } from '../../../shared/util/tax-years.service';
import { TranslocoService} from "@jsverse/transloco";
import { disableButton, enableButton } from '../../../shared/util/toggleButton';

const DISPLAY_DATE_FORMAT = "DD/MM/YYYY HH:mm";
declare var $: any;

interface ISource extends ListOfSource {
  id: number;
  name: string;
  type: string;
  requireAtlasId?: string;
  config?: string;
  status?: number;
  setSmartSource?: boolean;
  autoTagging?: boolean;
  enableDataStandardisation?: boolean;
  taxReturnTypeId?: string;
  categoryId?: string;
  subcategoryId?: string;
  subgroupId?: string;
  templateTypeId?: number;
  hidden?: boolean;
}

interface ITemplateType extends TemplateType {
  id?: number;
  templateGroup?: string;
  templateOrder?: number;
  templateType?: string;
  signatureRequired?: boolean;
  alias?: string;
  module?: string;
  updatedAt?: string;
  createdAt?: string;
}

interface IConfigSource extends ISource {
  displayUpdatedAt?: string;
  displayCreatedAt?: string;
  selected?: boolean;
}

interface ISourceQuestion {
  id?: number;
  question?: string;
}

interface IdeleteSources {
  id?: number;
  name?: string;
  sourceQuestions?: ISourceQuestion[];
}

@Component({
    selector: 'app-sources',
    templateUrl: './sources.component.html',
    styleUrls: ['./sources.component.scss'],
    standalone: false
})
export class SourcesComponent implements OnInit, OnDestroy {
  sources: ISource[];
  sourcesForConfigExport: IConfigSource[];
  @ViewChild('sourceModal') sourceModal: ElementRef;
  @ViewChild('configExportConfimationModal') configExportConfimationModal: ElementRef;
  @ViewChild('configDeleteCheckModal') configDeleteCheckModal: ElementRef;
  @ViewChild('configDeleteConfimationModal') configDeleteConfimationModal: ElementRef;
  source: ISource; // source in edit
  fileTypeOptions = [
    { value: 'csv', label: 'CSV / XLSX' },
    { value: 'pdf', label: 'PDF' },
    // { value: 'oneteam', label: 'ONETEAM' },
  ];
  form: UntypedFormGroup;
  configOperations = {
    export: "export",
    delete: "delete"
  }
  configOperation: string = this.configOperations.export;
  configOperationDate = "";
  configDeletePossible:boolean = false;
  configExportMode: boolean = false;
  configDeleteMode: boolean = false;
  deleteSources: IdeleteSources[] = [];
  entireListSelected = false;
  selectedSources = [];
  isProductionMode: boolean;
  uiConfig: TenantConfigService;
  taxYear:  number;
  taxReturnTypeOptions: any[];
  taxReturnAlias: string;
  categoryOptions: any[];
  categories: ICategory[];
  subcategoryOptions: any[] = [];
  subgroupOptions: any[] = [];
  templateTypeOptions: any[]=[];
  creatingBackup: boolean = false;
  subscriptions: Subscription[] = [];
  isSmartFormsEnabled: boolean;
  isSuperAdmin: boolean;

  constructor(
    private sourceService: SourceService,
    public userInfo: UserInfoService,
    public configState: ConfigStateService,
    private formBuilder: UntypedFormBuilder,
    private clientNotificationService: ClientNotificationService,
    private taxReturnTypeService: TaxReturnTypeSubTypeService,
    private taxReturnBuilderService: TaxReturnBuilderService,
    private taxYearsService: TaxYearsService,
    private translocoService: TranslocoService,
    private templateTypeService: TemplateTypeService,
  ) {
    this.isProductionMode = environment.production;
    this.isSuperAdmin = this.userInfo.isSuperAdmin;
  }

  get fileType(): AbstractControl {
    return this.form.get('fileType');
  }

  get name(): AbstractControl {
    return this.form.get('name');
  }

  get config(): AbstractControl {
    return this.form.get('config');
  }

  get requireAtlasId(): AbstractControl {
    return this.form.get('requireAtlasId');
  }

  // get setSmartSource(): AbstractControl {
  //   return this.form.get('setSmartSource');
  // }

  get autoTagging(): AbstractControl {
    return this.form.get('autoTagging');
  }

  get enableDataStandardisation(): AbstractControl {
    return this.form.get('enableDataStandardisation');
  }

  get taxReturnType(): AbstractControl {
    return this.form.get('taxReturnType');
  }

  get category(): AbstractControl {
    return this.form.get('category');
  }

  get subcategory(): AbstractControl {
    return this.form.get('subcategory');
  }

  get subgroup(): AbstractControl {
    return this.form.get('subgroup');
  }

  get templateType(): AbstractControl {
    return this.form.get('templateType');
  }

  ngOnInit() {
    this.subscriptions.push(
      this.configState.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
        this.isSmartFormsEnabled = this.configState.featureFlags.enableOneteamIntegration;
        this.translocoService.selectTranslate('tax-return-alias').subscribe(value => this.taxReturnAlias = value);
      })
    );

    this.subscriptions.push(this.getSources());
    this.subscriptions.push(this.getTemplateTypes());
    this.initForm();

    this.taxYear = this.taxYearsService.getCurrentTaxYear();
    this.subscriptions.push(this.taxReturnTypeService.getTaxReturnTypes().subscribe((options: any) => {
      this.taxReturnTypeOptions = options
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getSources() {
    return this.sourceService.getSources().subscribe((sources: any) => {
      const displayTypes = ['csv', 'prefill', 'questionnaire', 'traveldiary', 'pdf', 'oneteam']
      this.sources = sources.filter(s => displayTypes.includes(s.type.toLowerCase()));
      this.loadSourcesForConfigExport();
    });
  }

  getTemplateTypes()
  {
    return this.templateTypeService.getTemplateTypes().subscribe((data: ITemplateType[]) => {
      this.templateTypeOptions = [{
        templateType: "Select...",
        id: null
      }]
      this.templateTypeOptions = [...this.templateTypeOptions, ...data];
    });
  }

  loadSourcesForConfigExport(ids?: string[]) {
    this.sourcesForConfigExport = this.sources.map((source: any) => {
      if (ids && !ids.includes(source.id)) {
        return;
      }
      const updatedAt = moment(source.updatedAt);
      const createdAt = moment(source.createdAt);
      return {
        selected: false,
        displayUpdatedAt: updatedAt.format(DISPLAY_DATE_FORMAT).toString(),
        displayCreatedAt: createdAt.format(DISPLAY_DATE_FORMAT).toString(),
        ...source
      }
    }).filter(s => s);
  }

  openSourceModal(source?: Source) {
    // @ts-ignore TODO - fix incorrect types
    this.source = cloneDeep(source) || {
      id: null,
      name: '',
      type: null,
      requireAtlasId: true,
      // setSmartSource: false,
      autoTagging: false,
      enableDataStandardisation: false,
      taxReturnType: '',
      category: '',
      subcategory: '',
      subgroup: '',
      templateType:'',
      config: '',
      hidden: false
    };

    //existing source is smartSource if subgroupId is not null.
    // if (source && source.subgroupId) {
    //   this.source.setSmartSource = true
    // }else{
    //   this.source.setSmartSource = false
    // }
    this.initForm()
    // this.updateVlidator(this.source.setSmartSource)

    this.name.setValue(this.source.name);
    this.fileType.setValue(this.source.type);
    this.requireAtlasId.setValue(this.source.requireAtlasId);
    // this.setSmartSource.setValue(this.source.setSmartSource);
    this.autoTagging.setValue(this.source.autoTagging);
    this.enableDataStandardisation.setValue(this.source.enableDataStandardisation);
    this.taxReturnType.setValue(this.source.taxReturnTypeId);
    this.category.setValue(this.source.categoryId);
    this.subcategory.setValue(this.source.subcategoryId);
    this.subgroup.setValue(this.source.subgroupId);
    this.templateType.setValue(this.source.templateTypeId);
    this.config.patchValue(JSON.parse(this.source.config || '{}'));


    this.categoryOptions = [];
    this.subcategoryOptions = [];
    this.subgroupOptions = [];


    //init properties for existing smartSource
    // if (this.source.taxReturnTypeId) {
    //   const isExistingSmartSource = true
    //   this.getCategories(isExistingSmartSource)
    // }
    $(this.sourceModal.nativeElement).modal('show');
  }

  createBackup() {
    if(!this.creatingBackup){
      this.creatingBackup = true;
      this.sourceService.backupSourceConfig().subscribe(()=>{
        this.creatingBackup = false;
      }, error => {
        this.creatingBackup = false;
      });
    }
  }

  saveSource() {
    this.source.id
      ? this.sourceService.updateSource({
        id: this.source.id,
        name: this.name.value,
        type: this.fileType.value,
        userId: this.userInfo.id,
        requireAtlasId: this.requireAtlasId.value,
        templateTypeId: this.templateType.value,
        enableDataStandardisation: this.enableDataStandardisation.value ? 1 : 0,
        config: this.config.value,
        hidden: this.source.hidden,
      }).subscribe(() => {
        this.getSources();
      })
      : this.sourceService.addSource({
        id: null,
        name: this.name.value,
        type: this.fileType.value,
        userId: this.userInfo.id,
        requireAtlasId: this.requireAtlasId.value,
        config: this.config.value,
        subgroupId: this.subgroup.value,
        autoTagging: this.autoTagging.value,
        enableDataStandardisation: this.enableDataStandardisation.value,
        templateTypeId: this.templateType.value,
      }).subscribe(() => {
        this.getSources();
      });
  }

  saveSourceHidden(source: Source, $event: Event) {
    $event.preventDefault();
    this.sourceService.updateSource({ id: source.id, name: source.name, hidden: !source.hidden }).subscribe(() => {
      this.getSources();
    })
  }

  getHiddenTooltip() {
    if (!this.isSuperAdmin) {
      return 'Only super admins are allowed to Hide / Unhide columns.';
    } else {
      return '';
    }
  }

  checkSourceType() {
    if (this.fileType.value === 'pdf') {
      this.requireAtlasId.setValue(true);
    }

    // if (this.fileType.value === 'oneteam') {
    //   this.setSmartSource.setValue(true);
    //   this.updateVlidator(this.setSmartSource.value)
    // }else {
    //   this.setSmartSource.setValue(false);
    //   this.updateVlidator(this.setSmartSource.value)
    // }
  }

  // getCategories(isExistingSmartSource?: boolean) {
  //   //clear previous seleced value of category,subcategory,subgroup when taxReturnType changed
  //   if (!isExistingSmartSource) {
  //     this.category.setValue('');
  //     this.subcategoryOptions = []
  //     this.subgroupOptions = []
  //   }

  //   //get category options by seleted taxReturnType
  //   const taxReturnTypeId = Number(this.taxReturnType.value)
  //   const sub = taxReturnTypeId && this.taxReturnBuilderService.getCategories(taxReturnTypeId)
  //     .subscribe(results => {
  //       this.categoryOptions = results
  //       if (isExistingSmartSource) {
  //         this.getSubcategories(isExistingSmartSource)
  //       }
  //   });
  //   this.subscriptions.push(sub);
  // }

  getSubcategories(isExistingSmartSource?: boolean) {
    //clear previous seleced value of subcategory and subgroup when category changed
    if (!isExistingSmartSource) {
      this.subcategory.setValue('');
      this.subgroupOptions = []
    }

    //get category with subcategories and subgroups by selected category Id
    this.category.value && this.subscriptions.push(this.taxReturnBuilderService.getCategoryById(this.category.value, this.taxYear.toString()).subscribe(results => {
      this.subcategoryOptions = results.subcategories;
      if (isExistingSmartSource) {
        this.getSubgroups(isExistingSmartSource)
      }
    }));
  }

  getSubgroups(isExistingSmartSource?: boolean) {
    //clear previous seleced value of subgroup when category changed
    if (!isExistingSmartSource) {
      this.subgroup.setValue('');
    }
    this.subcategoryOptions.map(sc => {
      if (sc.id === this.subcategory.value) {
        this.subgroupOptions = []
        sc.subgroups.forEach(sg => {
          if (!sg.questions.find(_q => _q.isTab)) {
            this.subgroupOptions.push(sg)
          }
        })
      }
    })
  }

  toggleConfigOperationMode(operation){
    this.configOperation = operation;
    this.configOperationDate = "";
    this.loadSourcesForConfigExport();
    if(operation === this.configOperations.export){
      this.configDeleteMode = false;
      this.configExportMode = !this.configExportMode;
    } else if(operation === this.configOperations.delete){
      this.configExportMode = false;
      this.configDeleteMode = !this.configDeleteMode;
    } else {
      this.configExportMode = false;
      this.configDeleteMode = false;
    }
    // if(!this.configExportMode && !this.configDeleteMode){
    //   this.resetConfigFilters();
    // }
  }

  entireListSelection() {
    this.sourcesForConfigExport.forEach((templateType) => {
      templateType.selected = this.entireListSelected;
    });
  }

  listSelection() {
    this.sourcesForConfigExport.forEach((s) => {
      if (s.selected === false) return this.entireListSelected = false;
    })
  }

  filterSourcesOnDateChange() {
    if (!this.configOperationDate.length) {
      this.loadSourcesForConfigExport();
      return;
    }
    this.sourceService.getUpdatedSourcesByDate(this.configOperationDate).subscribe((ids) => {
      this.loadSourcesForConfigExport(ids);
    });
  }

  openConfigOperationConfimationModal(operation) {
    this.selectedSources = this.sourcesForConfigExport.map((s) => {
      if (s.selected) { return { name: s.name, displayUpdatedAt: s.displayUpdatedAt, id: s.id } }
    }).filter(tt => tt);
    if(operation === this.configOperations.export){
      $(this.configExportConfimationModal.nativeElement).modal("show");
      $(this.configExportConfimationModal.nativeElement).scrollTop($(this.configExportConfimationModal.nativeElement)[0].scrollHeight);
    } else if(operation === this.configOperations.delete){
      $(this.configDeleteCheckModal.nativeElement).modal("show");
      $(this.configDeleteCheckModal.nativeElement).scrollTop($(this.configDeleteCheckModal.nativeElement)[0].scrollHeight);
    }

  }

  clearSelectedSources() {
    this.entireListSelected = false;
    this.sourcesForConfigExport.forEach((s) => s.selected = false);
  }

  clearDeleteConfig() {
    $(this.configDeleteCheckModal.nativeElement).modal("hide");
    $(this.configDeleteConfimationModal.nativeElement).modal("hide");
  }

  downloadConfig() {
    if (!this.selectedSources.length) { return; };
    const sourceIds = this.selectedSources.map(s => s.id);
    this.sourceService.downloadConfigExportSource({ sourceIds }).subscribe(data => {
      if (!data.fileContent) {
        this.clientNotificationService.appendMessage("File couldn't be fetched, please try again later", "Download config", false);
        return;
      }
      var elem = window.document.createElement('a');
      elem.href = "data:application/json," + encodeURIComponent(data.fileContent);
      elem.download = data.fileName ? data.fileName : "package";
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
      this.clearSelectedSources();
      $(this.configExportConfimationModal.nativeElement).modal("hide");
    }, error => {
      $(this.configExportConfimationModal.nativeElement).modal("hide");

    })
  }

  updateValidator(isSmartSource?: boolean) {
    if (isSmartSource) {
      //set validator for subgroup if source is set to smartSource
      this.subgroup.setValidators(Validators.required)
    } else {
      this.subgroup.setValidators(null)
      this.subgroup.setErrors(null)
    }
  }

  deleteConfig({confirmed, event}) {
    if (!this.selectedSources.length) { return; };
    const sourceIds = this.selectedSources.map(s => s.id);
    disableButton(event.target);
    this.sourceService.deleteSourceConfig({ sourceIds, confirmed}).subscribe((data) => {
      enableButton(event.target);
      if(data.deleteConfig && confirmed && data.deleteConfig.deleted ){
        this.clearSelectedSources();
        this.clearDeleteConfig();
        window.location.reload();
      } else if(data.deleteConfig && !confirmed){
        const dialogDismissed = !$(this.configDeleteCheckModal.nativeElement).modal("is active");
        if (dialogDismissed) { return; }
        $(this.configDeleteCheckModal.nativeElement).modal("hide");
        this.configDeletePossible = data.deletePossible;
        this.deleteSources = data.deleteConfig?.sources;
        $(this.configDeleteConfimationModal.nativeElement).modal("show");
      } else {
        this.clearSelectedSources();
        this.clearDeleteConfig();
      }
    }, error => {
      enableButton(event.target);
      this.clearSelectedSources();
      this.clearDeleteConfig()
    })
  }

  private initForm() {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      fileType: ['', Validators.required],
      config: this.formBuilder.group({
        cache: false,
        adminOnly: false,
        subTypeRequired: false,
      }),
      requireAtlasId: ['', Validators.required],
      // setSmartSource: ['', Validators.required],
      autoTagging: [''],
      enableDataStandardisation: [''],
      taxReturnType: [''],
      category: [''],
      subcategory: [''],
      subgroup:[''],
      templateType:[''],
    });
  }
}
